'use client';

import React, { useEffect, useState } from 'react';
import {
  MapPinIcon,
  ClockIcon,
  TicketIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';


interface EventData {
  venue: string | null;
  address: string | null;
  postcode: string | null;
  phone: string | null;
  gates_open: string | null;
  event_finish: string | null;
}

function formatTime(t: string | null): string {
  if (!t) return '';
  // t is HH:MM:SS or HH:MM — return HH:MM AM/PM
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
}

export default function EventInfoCards() {
  const [eventData, setEventData] = useState<EventData>({
    venue: 'New Milton RFC',
    address: 'Normans Way, Ashley, BH25 5FN',
    postcode: null,
    phone: '01425 610401',
    gates_open: '11:00',
    event_finish: '16:30',
  });

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from('event_details')
      .select('venue, address, postcode, phone, gates_open, event_finish')
      .limit(1)
      .single()
      .then(({ data }) => {
        if (data) setEventData(data);
      });
  }, []);

  const gatesOpenDisplay = formatTime(eventData.gates_open) || '11:00 AM';
  const finishDisplay = formatTime(eventData.event_finish) || '4:30 PM';
  const addressDisplay = eventData.postcode
    ? `${eventData.address ?? ''}, ${eventData.postcode}`
    : eventData.address ?? 'Normans Way, Ashley, BH25 5FN';

  const infoItems = [
    {
      key: 'info-location',
      icon: MapPinIcon,
      label: 'Location',
      value: eventData.venue ?? 'New Milton RFC',
      sub: addressDisplay,
      color: 'bg-accent/10 text-accent',
    },
    {
      key: 'info-gates',
      icon: ClockIcon,
      label: 'Gates Open',
      value: gatesOpenDisplay,
      sub: `Event finishes at ${finishDisplay}`,
      color: 'bg-primary/10 text-primary',
    },
    {
      key: 'info-entry',
      icon: TicketIcon,
      label: 'Entry',
      value: 'Free Entry',
      sub: 'Donations welcome for club equipment',
      color: 'bg-secondary/10 text-secondary',
    },
    {
      key: 'info-contact',
      icon: PhoneIcon,
      label: 'Contact',
      value: eventData.phone ?? '01425 610401',
      sub: eventData.venue ?? 'New Milton RFC',
      color: 'bg-accent/10 text-accent',
    },
  ];

  return (
    <section>
      <h2 className="text-section-title text-foreground mb-4">Event Details</h2>
      <div className="grid grid-cols-2 gap-3">
        {infoItems?.map((item) => {
          const Icon = item?.icon;
          return (
            <div
              key={item?.key}
              className="bg-card rounded-2xl p-4 card-shadow border border-border"
            >
              <div className={`inline-flex p-2 rounded-xl mb-3 ${item?.color}`}>
                <Icon className="w-5 h-5" />
              </div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                {item?.label}
              </p>
              <p className="text-sm font-bold text-foreground leading-tight">{item?.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5 leading-tight">{item?.sub}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}